<template>
  <div>
    <!-- Page Header -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="page-title mb-0">
            <feather-icon
              icon="TruckIcon"
              size="24"
              class="mr-2"
            />
            Gestion des Ramasseurs
          </h1>
          <p class="text-muted mb-0">
            Consultez et gérez les ramasseurs de votre flotte logistique.
          </p>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-primary"
            class="mr-2"
            :disabled="isLoading"
            @click="fetchPickupeurs"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'rotate-animation': isLoading }"
            />
          </b-button>
          <b-button
            :to="{ name: 'pickupeur-create' }"
            variant="primary"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            <span class="ml-1">Nouveau ramasseur</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <b-row class="mb-4">
      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="h-100">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0 text-dark">
                {{ stats.total }}
              </h3>
              <p class="card-text mb-0">
                Total Ramasseurs
              </p>
            </div>
            <div class="avatar bg-light-secondary p-50">
              <feather-icon
                icon="UsersIcon"
                size="24"
                class="text-secondary"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="h-100">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0 text-success">
                {{ stats.active }}
              </h3>
              <p class="card-text mb-0">
                Actifs
              </p>
            </div>
            <div class="avatar bg-light-success p-50">
              <feather-icon
                icon="UserCheckIcon"
                size="24"
                class="text-success"
              />
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-3"
      >
        <b-card class="h-100">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h3 class="font-weight-bolder mb-0 text-info">
                {{ stats.withCars }}
              </h3>
              <p class="card-text mb-0">
                Avec Véhicule
              </p>
            </div>
            <div class="avatar bg-light-info p-50">
              <feather-icon
                icon="TruckIcon"
                size="24"
                class="text-info"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Search and Filters -->
    <b-card class="mb-4">
      <b-row>
        <b-col
          md="12"
          sm="12"
          class="mb-3"
        >
          <b-form-group label-class="form-label-premium" label="Recherche">
            <b-input-group>
              <b-form-input class="premium-input"
                v-model="searchTerm"
                placeholder="Nom, email, téléphone..."
                @input="onSearchChange"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  @click="fetchPickupeurs"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Data Table -->
    <b-card>
      <b-overlay
        :show="isLoading"
        rounded="lg"
        spinner-variant="primary"
      >
        <div class="premium-table-wrapper">
          <vue-good-table
          :columns="columns"
          :rows="filteredPickupeurs"
          :search-options="{ enabled: false }"
          :pagination-options="{
            enabled: true,
            perPage: 10,
            perPageDropdown: [5, 10, 20, 50],
            dropdownAllowAll: false,
          }"
          style-class="vgt-table striped"
        >
          <!-- Username Column -->
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'username'">
              <div class="d-flex align-items-center">
                <b-avatar
                  size="32"
                  variant="light-primary"
                  class="mr-2"
                >
                  {{ getInitials(props.row.username) }}
                </b-avatar>
                <div>
                  <span class="font-weight-bold">{{ props.row.username }}</span>
                  <br>
                  <small class="text-muted">{{ props.row.email }}</small>
                </div>
              </div>
            </span>

            <!-- Phone Column -->
            <span v-else-if="props.column.field === 'phone'">
              <feather-icon
                icon="PhoneIcon"
                size="14"
                class="mr-1 text-muted"
              />
              {{ props.row.phone || 'N/A' }}
            </span>

            <!-- Repository Column -->
            <span v-else-if="props.column.field === 'repository'">
              <b-badge
                v-if="props.row.repository_name"
                variant="light-info"
                pill
              >
                <feather-icon
                  icon="HomeIcon"
                  size="12"
                  class="mr-1"
                />
                {{ props.row.repository_name }}
              </b-badge>
              <span
                v-else
                class="text-muted"
              >Non assigné</span>
            </span>

            <!-- Car Column -->
            <span v-else-if="props.column.field === 'car'">
              <b-badge
                v-if="props.row.car"
                variant="light-secondary"
                pill
              >
                <feather-icon
                  icon="TruckIcon"
                  size="12"
                  class="mr-1"
                />
                {{ props.row.car.brand }} - {{ props.row.car.serial_number }}
              </b-badge>
              <span
                v-else
                class="text-muted"
              >Non assigné</span>
            </span>

            <!-- Status Column -->
            <span v-else-if="props.column.field === 'is_active'">
              <b-badge
                :variant="props.row.is_active ? 'light-success' : 'light-danger'"
                pill
              >
                {{ props.row.is_active ? 'Actif' : 'Inactif' }}
              </b-badge>
            </span>

            <!-- Actions Column -->
            <span v-else-if="props.column.field === 'actions'">
              <div class="d-flex align-items-center justify-content-center">
                <!-- View Details Button -->
                <b-button
                  v-b-tooltip.hover
                  title="Voir détails"
                  variant="flat-info"
                  size="sm"
                  class="btn-icon mr-50"
                  :to="{ name: 'pickupeur-show', params: { id: props.row.id } }"
                >
                  <feather-icon
                    icon="EyeIcon"
                    size="16"
                  />
                </b-button>

                <!-- Edit Button -->
                <b-button
                  v-b-tooltip.hover
                  title="Modifier"
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon mr-50"
                  :to="{ name: 'pickupeur-edit', params: { id: props.row.id } }"
                >
                  <feather-icon
                    icon="EditIcon"
                    size="16"
                  />
                </b-button>

                <!-- Toggle Activation Button -->
                <b-button
                  v-b-tooltip.hover
                  :title="props.row.is_active ? 'Désactiver' : 'Activer'"
                  :variant="props.row.is_active ? 'flat-danger' : 'flat-success'"
                  size="sm"
                  class="btn-icon mr-50"
                  @click="toggleActivation(props.row)"
                >
                  <feather-icon
                    :icon="props.row.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                    size="16"
                  />
                </b-button>

                <!-- Change Password Button -->
                <b-button
                  v-if="isAdmin"
                  v-b-tooltip.hover
                  title="Changer mot de passe"
                  variant="flat-warning"
                  size="sm"
                  class="btn-icon"
                  :to="{ name: 'pickupeur-change-password', params: { id: props.row.id } }"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="16"
                  />
                </b-button>
              </div>
            </span>

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- Empty State -->
          <template slot="emptystate">
            <div class="text-center py-5">
              <feather-icon
                icon="InboxIcon"
                size="48"
                class="text-muted mb-3"
              />
              <h5 class="text-muted">
                Aucun ramasseur trouvé
              </h5>
              <p class="text-muted">
                Ajoutez un nouveau ramasseur pour commencer.
              </p>
              <b-button
                :to="{ name: 'pickupeur-create' }"
                variant="primary"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-1"
                />
                Ajouter un ramasseur
              </b-button>
            </div>
          </template>
        </vue-good-table>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BOverlay,
  BBadge,
  BAvatar,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BOverlay,
    BBadge,
    BAvatar,
    VueGoodTable,
  },

  data() {
    return {
      isLoading: false,
      pickupeurs: [],
      searchTerm: '',
      user: {},
      columns: [
        {
          label: 'Ramasseur',
          field: 'username',
          sortable: true,
        },
        {
          label: 'Téléphone',
          field: 'phone',
          sortable: true,
        },
        {
          label: 'Dépôt',
          field: 'repository',
          sortable: false,
        },
        {
          label: 'Véhicule',
          field: 'car',
          sortable: false,
        },
        {
          label: 'Statut',
          field: 'is_active',
          sortable: true,
        },
        {
          label: 'Actions',
          field: 'actions',
          sortable: false,
          width: '180px',
          tdClass: 'text-center',
        },
      ],
    }
  },

  computed: {
    stats() {
      return {
        total: this.pickupeurs.length,
        active: this.pickupeurs.filter(p => p.is_active).length,
        withCars: this.pickupeurs.filter(p => p.car).length,
      }
    },
    isAdmin() {
      return this.user && this.user.role === 'admin'
    },
    filteredPickupeurs() {
      let result = this.pickupeurs

      // Apply search filter
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase()
        result = result.filter(p => p.username?.toLowerCase().includes(term)
          || p.email?.toLowerCase().includes(term)
          || p.phone?.includes(term))
      }

      return result
    },
  },

  created() {
    this.user = storeAuth.state.user
    this.fetchPickupeurs()
  },

  methods: {
    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    onSearchChange() {
      // Debounce search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        // Just filter locally
      }, 300)
    },

    async fetchPickupeurs() {
      this.isLoading = true
      try {
        const response = await axios.get('/api/auth/users/pickupeurs/')
        this.pickupeurs = Array.isArray(response.data) ? response.data : response.data.results || []

        // Transform data for display
        this.pickupeurs = this.pickupeurs.map(p => ({
          ...p,
          car_info: p.car ? `${p.car.brand || ''} - ${p.car.serial_number || ''}`.trim() : null,
        }))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pickupeurs:', error)
        this.showToast('danger', 'Erreur lors du chargement des ramasseurs')
      } finally {
        this.isLoading = false
      }
    },

    async toggleActivation(pickupeur) {
      try {
        await axios.put(`/api/auth/users/pickupeurs/toggle-activation/${pickupeur.id}/`)
        const index = this.pickupeurs.findIndex(p => p.id === pickupeur.id)
        if (index !== -1) {
          this.pickupeurs[index].is_active = !this.pickupeurs[index].is_active
          this.showToast('success', `Ramasseur ${this.pickupeurs[index].is_active ? 'activé' : 'désactivé'} avec succès`)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling activation:', error)
        this.showToast('danger', 'Erreur lors du changement de statut')
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

.rotate-animation {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
